import { createContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useI18nContext } from '../i18n/i18n-react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { showErrorToast } from './Toast';
import Toast from 'react-native-toast-message';

interface NetworkToastContextType {
  /**
   * Change the toast message. If the message has changed, or the 2nd param is set, the toast will be un-dismissed.
   *
   * @param message New message to show. If this is different to the old message, toast will un-dismiss.
   * @param forceUndismiss If true, toast will un-dismiss regardless of whether the message changed.
   */
  changeToastMessage: (message: string, forceUndismiss?: boolean) => void;
}

export const NetworkToastContext = createContext<NetworkToastContextType>({
  changeToastMessage: () => {
    console.error('NetworkToastContext used outside of provider');
  }
});

/**
 * Manages a toast at the top conveying network connectivity issues.
 *
 * Automatically shows a toast when network connection is first lost. This can be dismissed, but shows itself
 * again on screen transitions. The toast can additionally be shown and the message changed using a callback
 * accessed via context.
 */
export const NetworkToastProvider = ({
  children,
  navState
}: {
  children: ReactNode;
  /** The current route - when this changes the toast is un-dismissed. */
  navState: string;
}) => {
  const { LL: translate } = useI18nContext();

  // Track whether we want to show a toast
  const { isConnected } = useNetworkStatus();

  // Track whether the user has temporarily dismissed the toast
  const [dismissed, setDismissed] = useState(false);

  // Track which message to display
  const defaultToastMessage = translate.errorModals.thisAppRequiresAnInternetConnection();
  const [toastMessage, setToastMessage] = useState<string>(defaultToastMessage);

  // When the route changes, un-dismiss
  useEffect(() => {
    setDismissed(false);
  }, [isConnected, navState]);

  // Reset to default state when we come back online, ready for the next usage.
  useEffect(() => {
    if (isConnected) {
      setDismissed(false);
      setToastMessage(defaultToastMessage);
    }
  }, [isConnected, defaultToastMessage]);

  // Callback for users of this context to change the toast message, possibly un-dismissing it.
  const changeToastMessage = useCallback(
    (message: string, forceUndismiss?: boolean) => {
      setToastMessage(message);
      if (message !== toastMessage || forceUndismiss) {
        setDismissed(false);
      }
    },
    [toastMessage]
  );

  useEffect(() => {
    // Only show the toast when the device transitions to offline
    if (!isConnected && !dismissed) {
      showErrorToast({
        title: translate.errorModals.internetConnectionLost(),
        message: toastMessage,
        persist: true,
        onPress: () => {
          setDismissed(true);
          Toast.hide();
        }
      });
    }

    // Hide any existing toast when reconnected or dismissed
    if (isConnected || dismissed) {
      Toast.hide();
    }
  }, [isConnected, dismissed, toastMessage, translate]);

  return (
    <NetworkToastContext.Provider value={{ changeToastMessage }}>
      {children}
    </NetworkToastContext.Provider>
  );
};
